/**
 * Owner: Masa Bayu
 * File: pdf-text-object.ts
 * Date:Time: 2026-09-30 13:35
 * Description: One uncompressed PDF text showing into existing Document IR fields.
 */

import type { QXDocumentIR } from '@qx/qx-document-ir';
import { advanceUnitsForText, resolvePdfFontGlyphWidths } from './pdf-glyph-widths';
import { parsePdfLayoutToIR } from './pdf-layout-parse';

export type QXPdfTextPlacement = {
  readonly sourceObjectId: string;
  readonly text: string;
  readonly pageNumber: number;
  readonly showingIndex: number;
  readonly rotation: 0;
  readonly fontName: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly placed: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
};

export type QXPdfTextImport = QXPdfTextPlacement & {
  readonly document: QXDocumentIR;
};

const latin1 = new TextDecoder('latin1');

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array | null } {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  const at = marked >= 0 ? marked + 1 : 0;
  if (marked < 0 && indexOfBytes(file, bytesOf(`${String(id)} 0 obj`)) !== 0) {
    throw new Error(`PDF object ${String(id)} is missing`);
  }
  const slice = file.subarray(at);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  const endAt = indexOfBytes(slice, bytesOf('endobj'));
  if (streamAt < 0 || (endAt >= 0 && endAt < streamAt)) {
    return { dict: textOf(slice, 0, endAt < 0 ? slice.length : endAt), stream: null };
  }
  const dict = textOf(slice, 0, streamAt);
  const length = dict.match(/\/Length\s+(\d+)/);
  if (!length?.[1]) throw new Error(`PDF object ${String(id)} stream has no length`);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  return { dict, stream: slice.subarray(dataAt, dataAt + Number(length[1])) };
}

function pdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function literalOf(block: string): string {
  let text = '';
  for (const item of block.matchAll(/\((?:\\.|[^)\\])*\)/g)) {
    text += (item[0] ?? '').slice(1, -1).replace(/\\(.)/g, '$1');
  }
  return text;
}

/**
 * Reads the first clear axis-aligned text showing on one uncompressed page.
 * A rotated showing is refused. A missing glyph width is refused.
 */
export function importOnePdfText(bytes: Uint8Array, pageNumber: number): QXPdfTextImport {
  if (!Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('pageNumber must be a positive integer');
  const file = Uint8Array.from(bytes);
  const boxAt = indexOfBytes(file, bytesOf('/MediaBox'));
  if (boxAt < 0) throw new Error('PDF page has no MediaBox');
  const box = textOf(file, boxAt, boxAt + 120).match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const pageWidth = Number(box?.[1] ?? 0);
  const pageHeight = Number(box?.[2] ?? 0);
  if (pageWidth <= 0 || pageHeight <= 0) throw new Error('PDF MediaBox is empty');
  const near = textOf(file, Math.max(0, boxAt - 800), Math.min(file.length, boxAt + 1500));
  const contentsId = Number(near.match(/\/Contents\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  if (!contentsId) throw new Error('PDF page has no content stream');
  const content = readStream(file, contentsId);
  if (/\/Filter\b/.test(content.dict) || !content.stream) throw new Error('PDF content stream is not plain text');
  const stream = textOf(content.stream, 0, content.stream.length);
  const showings = [...stream.matchAll(/BT([\s\S]*?)ET/g)];
  let chosen: { readonly index: number; readonly font: string; readonly size: number; readonly x: number; readonly pdfY: number; readonly text: string } | null = null;
  for (let index = 0; index < showings.length; index += 1) {
    const block = showings[index]?.[1] ?? '';
    const face = block.match(/\/(F\d+)\s+([\d.]+)\s+Tf/);
    const place = block.match(/1\s+0\s+0\s+1\s+([-\d.]+)\s+([-\d.]+)\s+Tm/);
    const text = literalOf(block).trim();
    if (!face?.[1] || !face[2] || !place?.[1] || !place[2] || !/[A-Za-z]{3,}/.test(text)) continue;
    chosen = { index, font: face[1], size: Number(face[2]), x: Number(place[1]), pdfY: Number(place[2]), text };
    break;
  }
  if (!chosen) throw new Error('Uncompressed page has no clear text object');
  const fontId = Number(near.match(new RegExp(`\\/${chosen.font}\\s+(\\d+)\\s+0\\s+R`))?.[1] ?? 0);
  if (!fontId) throw new Error(`PDF font ${chosen.font} has no object`);
  const font = readStream(file, fontId).dict;
  const base = font.match(/\/BaseFont\s*\/([^\s/]+)/)?.[1];
  if (!base) throw new Error('PDF font has no BaseFont');
  const fontName = pdfName(base);
  const glyph = resolvePdfFontGlyphWidths(font, (id) => readStream(file, id).dict);
  const units = advanceUnitsForText(glyph, chosen.text);
  const placed = {
    x: chosen.x,
    y: pageHeight - chosen.pdfY - chosen.size,
    width: (units * chosen.size) / 1000,
    height: chosen.size,
  };
  const sourceObjectId = `txt_${chosen.font}_${String(chosen.index)}`;
  const bold = fontName.endsWith(',Bold') || fontName.endsWith('-Bold') || fontName.endsWith(' Bold');
  const italic = /Italic/.test(fontName);
  const document = parsePdfLayoutToIR({
    sourceHash: 'sha256:qxeditor-poc-text-mirror-01',
    pages: [
      {
        pageNumber,
        width: pageWidth,
        height: pageHeight,
        blocks: [
          {
            id: sourceObjectId,
            kind: 'text',
            text: chosen.text,
            bbox: placed,
            lines: [
              {
                text: chosen.text,
                bbox: placed,
                runs: [
                  {
                    text: chosen.text,
                    fontName,
                    fontSize: chosen.size,
                    bold,
                    italic,
                    bbox: placed,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  });
  return {
    document,
    sourceObjectId,
    text: chosen.text,
    pageNumber,
    showingIndex: chosen.index,
    rotation: 0,
    fontName,
    fontSize: chosen.size,
    bold,
    italic,
    placed,
  };
}
